import React, { createContext, useContext, useState, useEffect } from 'react';

const DemoContext = createContext();

const CLAVE = 'aturno_demo_bookings';

/**
 * Una fecha relativa a hoy, en 'YYYY-MM-DD'.
 *
 * Armada con las partes locales y NO con `toISOString().split('T')[0]`: a UTC−3
 * eso corre la fecha al día siguiente para cualquier hora a partir de las 21.
 */
function enDias(dias) {
  const d = new Date();
  d.setDate(d.getDate() + dias);
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mes}-${dia}`;
}

/** Hace `horas` horas, en ISO. Para el "Reservado hace…" de la tarjeta. */
const haceHoras = (horas) => new Date(Date.now() - horas * 3600 * 1000).toISOString();

/**
 * Los turnos con los que arranca la demo.
 *
 * `/demo/dashboard` mostraba "No hay turnos", así que la pantalla principal del
 * panel no se podía ver ni revisar sin una cuenta real con datos cargados.
 * `check-ui.js` la recorre y sólo comprobaba una pantalla vacía.
 *
 * Cada uno enciende una rama distinta de la tarjeta, que es lo que los hace
 * servir para revisar un cambio:
 *
 *   1. confirmado, con personal y lugar   → la fila completa
 *   2. pendiente, con DNI y obra social   → la línea de paciente
 *   3. esperando la seña, sin personal    → la retención y "Cualquier personal"
 *   4. de ayer                            → la marca de "Pasado"
 *
 * Van a mañana y pasado y no a hoy: un turno de hoy a las 11:30 ya pasó si la
 * demo se abre a la tarde, y el filtro por defecto lo esconde.
 */
const TURNOS_DE_EJEMPLO = [
  {
    id: 'demo-1',
    time: '11:30',
    date: enDias(1),
    customerName: 'Pitu Ehrman',
    customerPhone: '+54 9 11 3003-2002',
    customerEmail: 'pitu@ejemplo.com',
    serviceName: 'Dentista',
    duration: 30,
    price: 100,
    status: 'confirmed',
    staffName: 'Matias Calo',
    staffId: 'demo-staff-1',
    resourceName: 'Consultorio 1',
    code: 'DEMO1234',
    createdAt: haceHoras(23),
  },
  {
    id: 'demo-2',
    time: '13:00',
    date: enDias(1),
    customerName: 'Carolina Benegas',
    customerPhone: '+54 9 11 4412-8890',
    customerEmail: 'caro@ejemplo.com',
    serviceName: 'Consulta de seguimiento',
    duration: 45,
    price: 18000,
    status: 'pending',
    staffName: 'Matias Calo',
    staffId: 'demo-staff-1',
    resourceName: 'Consultorio 2',
    code: 'DEMO5678',
    createdAt: haceHoras(3),
    dni: '32.145.678',
    obraSocial: 'OSDE',
    numeroDeAfiliado: '61-4412889-01',
  },
  {
    id: 'demo-3',
    time: '16:15',
    date: enDias(2),
    customerName: 'Rodrigo Fernández',
    customerPhone: '+54 9 351 555-7788',
    customerEmail: 'rodri@ejemplo.com',
    serviceName: 'Primera consulta',
    duration: 60,
    price: 25000,
    status: 'pending_deposit',
    resourceName: 'Consultorio 1',
    code: 'DEMO9012',
    createdAt: haceHoras(0.2),
    // La retención vence a los 30 minutos, igual que el link de MercadoPago.
    holdExpiresAt: new Date(Date.now() + 18 * 60 * 1000).toISOString(),
  },
  {
    id: 'demo-4',
    time: '09:00',
    date: enDias(-1),
    customerName: 'Ana López',
    customerPhone: '+54 9 11 5555-1234',
    customerEmail: 'ana@ejemplo.com',
    serviceName: 'Control',
    duration: 30,
    price: 12000,
    status: 'confirmed',
    staffName: 'Matias Calo',
    staffId: 'demo-staff-1',
    code: 'DEMO3456',
    createdAt: haceHoras(50),
  },
];

export const useDemo = () => {
  const context = useContext(DemoContext);
  if (!context) {
    throw new Error('useDemo must be used within a DemoProvider');
  }
  return context;
};

/**
 * Turnos de la demo.
 *
 * Guarda los turnos que se sacan desde /demo/business para que aparezcan en
 * /demo/dashboard, sin tocar la base de datos.
 *
 * Se persisten en localStorage: antes vivían solo en memoria, así que
 * cualquier recarga de la página —o el refresco automático del servidor de
 * desarrollo— borraba todo. En una demostración en vivo delante de un cliente
 * eso es exactamente lo que no puede pasar.
 */
export const DemoProvider = ({ children }) => {
  const [demoBookings, setDemoBookings] = useState(() => {
    try {
      const guardado = localStorage.getItem(CLAVE);
      // Sin nada guardado se arranca con los ejemplos, no vacío: un panel de
      // turnos sin turnos no muestra el producto y no se puede revisar.
      return guardado ? JSON.parse(guardado) : TURNOS_DE_EJEMPLO;
    } catch (error) {
      console.warn('No se pudieron leer los turnos de la demo:', error);
      return TURNOS_DE_EJEMPLO;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(CLAVE, JSON.stringify(demoBookings));
    } catch (error) {
      console.warn('No se pudieron guardar los turnos de la demo:', error);
    }
  }, [demoBookings]);

  const addDemoBooking = (booking) => {
    const newBooking = {
      ...booking,
      id: Date.now().toString(),
      status: booking.status || 'pending',
      createdAt: new Date().toISOString(),
    };
    setDemoBookings((prev) => [...prev, newBooking]);
    return newBooking;
  };

  const updateBookingStatus = (bookingId, status) => {
    setDemoBookings((prev) =>
      prev.map((booking) => (booking.id === bookingId ? { ...booking, status } : booking))
    );
  };

  /** Borra un turno de la demo, como el borrado definitivo del panel real. */
  const borrarDemoBooking = (bookingId) => {
    setDemoBookings((prev) => prev.filter((booking) => booking.id !== bookingId));
  };

  /** Deja la demo en cero. Útil entre una presentación y la siguiente. */
  const resetDemo = () => {
    setDemoBookings(TURNOS_DE_EJEMPLO);
    try {
      localStorage.removeItem(CLAVE);
    } catch (error) {
      console.warn('No se pudieron borrar los turnos de la demo:', error);
    }
  };

  const value = {
    demoBookings,
    addDemoBooking,
    updateBookingStatus,
    borrarDemoBooking,
    resetDemo,
  };

  return <DemoContext.Provider value={value}>{children}</DemoContext.Provider>;
};
